export const automationCodes = [
  'GLOBAL_KILL_SWITCH',
  'CUSTOMER_ONBOARDING',
  'CUSTOMER_USER_INVITATION',
  'RENEWAL_OUTREACH',
  'CLIENT_INTAKE_EMAIL',
  'HUBSPOT_SYNC',
  'HOLDED_SYNC',
];

export const automationModeOptions = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'AUTOMATIC', label: 'Automatic' },
];

const contextualOnlyModeOptions = [
  { value: 'MANUAL', label: 'Always contextual' },
];

export const getAutomationModeOptions = code => (
  code === 'CUSTOMER_USER_INVITATION' ? contextualOnlyModeOptions : automationModeOptions
);

export const getGovernanceDataPaths = section => {
  if (section === 'audit') return ['audit'];
  if (section === 'templates') return ['automation-workspace', 'customers'];
  return ['automation-workspace'];
};

const definitions = {
  GLOBAL_KILL_SWITCH: {
    label: 'Global emergency stop',
    action: 'Blocks every new Commercial workflow before it can create data or send an email.',
    triggers: [{
      code: 'SAVED_ACTIVE',
      type: 'SAFETY CONTROL',
      label: 'An administrator activates the emergency stop',
      detail: 'The stop applies immediately to every new contextual action, event, and scheduled check.',
    }],
    prerequisites: ['No additional setup is required.'],
    limitation: 'This is the only enable/disable switch. Active means stop every new Commercial workflow.',
    readiness: { label: 'SAFETY CONTROL', tone: 'critical' },
  },
  CUSTOMER_ONBOARDING: {
    label: 'Customer onboarding',
    action: 'Creates or reuses the customer and account, grants support access, creates licence users, and sends their welcome messages.',
    triggers: [
      {
        code: 'START_ONBOARDING',
        type: 'MANUAL',
        label: 'Sales · Move to WON or Start onboarding',
        detail: 'Moving to WON opens the account, support identity, client-user, and email review in Sales. Start onboarding opens the same setup directly.',
      },
      {
        code: 'OPPORTUNITY_WON',
        type: 'AUTOMATIC',
        label: 'Sales · Opportunity moved to WON',
        detail: 'Automatic mode starts onboarding when the opportunity has the required customer contact data.',
      },
    ],
    prerequisites: ['An opportunity and account name.', 'At least one customer contact email.', 'An active ONBOARDING template.'],
    limitation: 'Manual never runs from this page. Automatic uses the WON event; incomplete opportunities return to Sales for correction.',
    readiness: { label: 'SALES', tone: 'event' },
  },
  CUSTOMER_USER_INVITATION: {
    label: 'Customer user invitation',
    action: 'Creates the licence user, viewer access, customer contact, and welcome message together.',
    triggers: [
      {
        code: 'CUSTOMER_DETAIL_MANUAL',
        type: 'MANUAL',
        label: 'Customer detail · Invite client user',
        detail: 'The user enters the name and email and confirms the complete access, contact, and welcome-message action in the invitation dialog.',
      },
      {
        code: 'CUSTOMER_DETAIL_AUTOMATIC',
        type: 'AUTOMATIC',
        label: 'Customer detail · Valid invitation submitted',
        detail: 'After the required name and email are submitted, the account access, contact, and welcome message are completed together without a second operations-page review.',
      },
    ],
    prerequisites: ['A customer linked to a platform account.', 'A unique user email and name.', 'An active ONBOARDING template.'],
    limitation: 'There is no Automation-page run. The customer page is the only place to supply and confirm the user details.',
    readiness: { label: 'CUSTOMER DETAIL', tone: 'event' },
  },
  RENEWAL_OUTREACH: {
    label: 'Renewal outreach',
    action: 'Loads renewal and contact data, renders the RENEWAL template, and records delivery.',
    triggers: [
      {
        code: 'RENEWAL_PAGE',
        type: 'MANUAL',
        label: 'Renewals · Send reminder',
        detail: 'The user starts outreach from the renewal row and receives the result on that page.',
      },
      {
        code: 'CUSTOMER_DETAIL',
        type: 'MANUAL',
        label: 'Customer detail · Send reminder',
        detail: 'The user starts the same workflow from the customer’s upcoming renewal.',
      },
      {
        code: 'SCHEDULE',
        type: 'AUTOMATIC',
        label: 'Daily 90, 30 and 7-day renewal check',
        detail: 'Automatic mode starts outreach once per renewal threshold using a stable idempotency key.',
      },
    ],
    prerequisites: ['A renewal linked to an active customer.', 'A customer email contact.', 'An active RENEWAL template.'],
    limitation: 'Manual sends only from Renewals or Customer detail. The daily threshold check runs only in Automatic.',
    readiness: { label: 'RENEWALS', tone: 'event' },
  },
  CLIENT_INTAKE_EMAIL: {
    label: 'Client intake email',
    action: 'Delivers the secure fiscal and technical intake link when an opportunity is won.',
    triggers: [{ code: 'WON_INTAKE', type: 'AUTOMATIC', label: 'Sales · Opportunity moved to WON', detail: 'Creates a seven-day secure link and delivers it only when every rollout gate passes.' }],
    prerequisites: ['An active CLIENT_INTAKE template.', 'A verified sender.', 'Zero recent bounce or complaint events.'],
    limitation: 'Keep disabled for the test period. An optional allowlist restricts delivery to pilot recipients.',
    readiness: { label: 'DELIVERY', tone: 'event' },
  },
  HUBSPOT_SYNC: {
    label: 'HubSpot customer sync',
    action: 'Upserts the approved customer and billing contact through the HubSpot CRM API.',
    triggers: [{ code: 'INTAKE_APPROVED', type: 'AUTOMATIC', label: 'Delivery · Intake approved', detail: 'Queues an idempotent customer upsert.' }],
    prerequisites: ['HUBSPOT_PRIVATE_APP_TOKEN in the backend environment.', 'Company and contact read/write scopes.'],
    limitation: 'Disabled mode retains jobs in the outbox without making provider writes.',
    readiness: { label: 'INTEGRATION', tone: 'event' },
  },
  HOLDED_SYNC: {
    label: 'Holded customer sync',
    action: 'Upserts approved fiscal and billing data as a Holded client contact.',
    triggers: [{ code: 'INTAKE_APPROVED', type: 'AUTOMATIC', label: 'Delivery · Intake approved', detail: 'Queues an idempotent contact upsert.' }],
    prerequisites: ['HOLDED_API_KEY in the backend environment.', 'Contacts read/write permission.'],
    limitation: 'Disabled mode retains jobs in the outbox without making provider writes or invoices.',
    readiness: { label: 'INTEGRATION', tone: 'event' },
  },
};

const fallbackDefinition = code => ({
  label: String(code || 'Unknown automation').replaceAll('_', ' '),
  action: 'Runs the configured Commercial workflow.',
  triggers: [{
    code: 'CONTEXT_PAGE',
    type: 'MANUAL',
    label: 'The relevant Commercial page',
    detail: 'Complete this workflow from its business context.',
  }],
  prerequisites: ['Required input must be supplied on the relevant page.'],
  limitation: 'No automatic trigger is documented.',
  readiness: { label: 'CONTEXTUAL', tone: 'neutral' },
});

export const getAutomationDefinition = code => {
  const definition = definitions[code] || fallbackDefinition(code);
  if (code !== 'CUSTOMER_USER_INVITATION') return definition;
  return {
    ...definition,
    triggers: definition.triggers.filter(trigger => trigger.type !== 'AUTOMATIC'),
    limitation: 'This workflow has no independent background trigger. The customer page is the only place to supply, review, and confirm the user details.',
    readiness: { label: 'ALWAYS CONTEXTUAL', tone: 'event' },
  };
};
export const getAutomationReadiness = code => getAutomationDefinition(code).readiness;

export function getAutomationPolicyMessage(setting, emergencyStopActive = false, code) {
  if (setting.enabled === false && !emergencyStopActive) return 'Disabled - queued work is retained but no external delivery starts.';
  if (code === 'CUSTOMER_USER_INVITATION' && !emergencyStopActive) {
    return 'Always contextual - users review and confirm this action from Customer detail.';
  }
  if (emergencyStopActive) return 'Emergency stop active — every new automation is blocked.';
  if (setting.mode === 'AUTOMATIC') return 'Automatic — the documented event or schedule starts the workflow.';
  return 'Manual — users start and confirm this workflow on the page named below.';
}

export function getLatestAutomationRun(code, runs = []) {
  return runs
    .filter(run => run.code === code)
    .reduce((latest, run) => (!latest || new Date(run.createdAt) > new Date(latest.createdAt) ? run : latest), null);
}

const asRecord = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);
const normalizeEmail = value => String(value || '').trim().toLowerCase();

export function getWorkflowRetryDetails(run) {
  if (!run || run.status !== 'FAILED') return null;
  if (!['CUSTOMER_ONBOARDING', 'CUSTOMER_USER_INVITATION'].includes(run.code)) return null;
  const payload = asRecord(asRecord(run.input)?.payload);
  const output = asRecord(run.output);
  if (!payload || !output) return null;

  let recipients = [];
  if (run.code === 'CUSTOMER_USER_INVITATION') {
    if (run.entityType !== 'CUSTOMER' || output.customerId !== run.entityId) return null;
    const requestedEmail = normalizeEmail(payload.email);
    const failed = (Array.isArray(output.steps) ? output.steps : [])
      .map(asRecord)
      .find(step => step?.code === 'ONBOARDING_MESSAGE'
        && step.status === 'FAILED'
        && normalizeEmail(step.recipient) === requestedEmail);
    if (!requestedEmail || !failed) return null;
    recipients = [requestedEmail];
  } else {
    if (run.entityType !== 'OPPORTUNITY' || asRecord(output.opportunity)?.id !== run.entityId) return null;
    const requested = new Set((Array.isArray(payload.licenseUsers) ? payload.licenseUsers : [])
      .map(asRecord)
      .map(user => normalizeEmail(user?.email))
      .filter(Boolean));
    recipients = (Array.isArray(output.emailRuns) ? output.emailRuns : [])
      .map(asRecord)
      .filter(emailRun => emailRun?.status === 'FAILED')
      .map(emailRun => normalizeEmail(emailRun?.recipient))
      .filter((email, index, values) => requested.has(email) && values.indexOf(email) === index);
    if (!recipients.length) return null;
  }

  const recipientLabel = recipients.join(', ');
  return {
    recipients,
    description: `Rotate the existing licence password${recipients.length === 1 ? '' : 's'} and send ${recipients.length === 1 ? 'a' : ''} fresh welcome email${recipients.length === 1 ? '' : 's'} to ${recipientLabel}.`,
  };
}

export const hasUnsavedAutomationChanges = (source, value) => (
  Boolean(source.enabled) !== Boolean(value.enabled) || source.mode !== value.mode
  || JSON.stringify(source.configuration || {}) !== JSON.stringify(value.configuration || {})
);

export const buildTemplatePreviewContext = (summary = {}, detail = {}) => {
  const customer = detail.customer || summary;
  const contacts = Array.isArray(detail.contacts) ? detail.contacts : [];
  const contact = contacts.find(item => item.isPrimary && item.email)
    || contacts.find(item => item.email)
    || null;
  return {
    ...summary,
    ...customer,
    name: contact?.name || customer.name,
    company: customer.name,
    email: contact?.email || '',
    renewalDate: detail.upcomingRenewal?.renewalDate || '',
  };
};
